/**
 * 가로로 미는 줄 · 슬라이드의 **오른쪽 «더 있음» 화살표** — 사장님 2026-10-21
 *   «가로 슬라이드가 들어가면 우측에는 화살표가 있어야 한다(전체점검)».
 *
 * 표·코드 상자에는 이미 있다(viewer_tablescroll.ts · F620/F627-1). 그 밖의 가로 스크롤
 * (목록 도구줄 · 통계 줄 · 카드 격자 · ARTIS 카드 윗줄 · QR 페이지 슬라이드 · 랜딩 폰 슬라이드)에는
 * **없었다** — 막대가 얇거나(4px) 아예 숨겨져 있어서(슬라이드) «오른쪽에 더 있다» 를 알 길이 없었다.
 *
 * 같은 부품을 쓴다(0-0):
 *   · 표식 = viewer_tablescroll 의 `mark`(`nxe-tw--scroll` · `nxe-tw--more`) — 오른쪽에 더 있을 때만 뜨고,
 *     끝까지 밀면 사라지고, 스크롤할 것이 없으면 아무것도 안 붙는다.
 *   · 모양 = 목록 표 화살표(nx-list.css `.nx-list__body > .nxe-twa`)와 같은 값 — 28px 네모 · 반투명 짙은 바탕 · 흰 꺾쇠 · 라운드 0.
 *
 * 어떻게 —
 *   ① 스크롤 상자를 **안 밀리는 껍데기**(`.nx-hsx`)로 감싸고, 그 안에 레일(`.nxe-twa`)을 먼저 넣는다
 *      (표와 같은 까닭: 화살표가 스크롤 상자 **안**에 있으면 같이 밀려 간다).
 *   ② 화살표는 껍데기 오른쪽 **세로 가운데**. 높이가 28px 보다 낮은 줄(통계 줄)에서는 줄 높이만큼.
 *   ③ 클릭을 안 먹는다 — 줄을 손으로 밀 때 가로막지 않게(표와 같다).
 *      ★ 슬라이드(`data-nx-hs="slide"`)만은 누르면 **한 장 넘긴다** — PC 에서는 손으로 밀 수 없으니
 *        화살표가 곧 «다음» 단추다.
 *   ④ 목록을 갈아 끼우거나(목록 fetch) 카드 한 장을 바꿔 끼우면(출판 폴러) 새 상자가 생긴다 —
 *      문서 변화를 보고(MutationObserver · 한 프레임에 한 번) 새 상자에만 붙인다(두 번 안 붙는다).
 *
 * 대상 = 아래 `HS_SEL` 하나(선택자 정본). 새 가로 스크롤을 만들면 `data-nx-hs` 를 달거나 여기에 더한다.
 * ★ 사이트 밖 단독 페이지(QR 작품 페이지 · PalmArtis 랜딩 · PalmArtis 편집기)는 `/shared/dist/hscroll_page.js` 로 같은 코드를 싣는다
 *   (편집기는 서버가 열 때 끼워 넣는다 — routes/artis.py edit · PC 에서 파일로 열면 화살표 없음).
 * ★ 편집기는 줄마다 order·flex 를 **껍데기에** 걸어 두고 `.nx-hsx` 껍데기를 미리 세운다 — 그런 곳은 다시 감싸지 않는다.
 */
import { mark, moreRight } from './viewer_tablescroll';

export const HS_SEL = '[data-nx-hs], .nx-list__toolscroll, .nx-list__stats, .nx-doxcard__scroll';
export const HS_OUTER = 'nx-hsx';

const CSS = [
  '.nx-hsx{position:relative;min-width:0}',
  '.nx-list__tools>.nx-hsx{flex:1 1 auto}',
  '.nx-hsx>.nxe-twa{display:none;position:absolute;top:0;right:0;bottom:0;width:28px;z-index:5;pointer-events:none}',
  '.nx-hsx:has(>.nxe-tw--more)>.nxe-twa{display:block}',
  '.nx-hsx>.nxe-twa>i{position:absolute;right:0;top:50%;transform:translateY(-50%);display:block;width:28px;height:min(28px,100%);',
  'border-radius:0;background:rgba(15,23,42,.45);color:#fff}',
  '.nx-hsx>.nxe-twa>i::after{content:"";position:absolute;left:9px;top:50%;width:8px;height:8px;margin-top:-4px;',
  'border-top:2px solid #fff;border-right:2px solid #fff;transform:rotate(45deg);box-sizing:content-box}',
  '.nx-hsx--slide>.nxe-twa{pointer-events:auto;cursor:pointer}',
].join('');

function injectCss(): void {
  if (document.getElementById('nx-hs-css')) return;
  const st = document.createElement('style');
  st.id = 'nx-hs-css';
  st.textContent = CSS;
  document.head.appendChild(st);
}

/** `root` 안의 가로 스크롤 상자에 화살표를 단다. 반환 = 새로 단 수. 두 번 불러도 안전하다. */
export function enableHScroll(root: ParentNode = document): number {
  injectCss();
  let n = 0;
  root.querySelectorAll<HTMLElement>(HS_SEL).forEach((w) => {
    if (w.dataset.nxHsDone === '1') { mark(w); return; }
    if (w.closest('.ProseMirror')) return;          // 편집기가 쥔 것은 안 감싼다(viewer_tablescroll 와 같다)
    w.dataset.nxHsDone = '1';
    n += 1;
    const slide = w.dataset.nxHs === 'slide';
    // ★ 껍데기를 화면이 **미리** 세워 둔 곳(PalmArtis 편집기 — 줄마다 order·flex 가 껍데기에 걸려 있다)은 그대로 쓴다.
    //   남이 짠 배치를 감싸서 흔들지 않는다(viewer_tablescroll 가 이미 `.nxe-twx` 안의 표를 다시 안 감싸는 것과 같다).
    const pre = w.parentElement && w.parentElement.classList.contains(HS_OUTER) ? w.parentElement : null;
    const outer = pre || document.createElement('div');
    outer.classList.add(HS_OUTER);
    if (slide) outer.classList.add('nx-hsx--slide');
    if (!pre) w.replaceWith(outer);
    const rail = document.createElement('div');
    rail.className = 'nxe-twa';
    rail.setAttribute('aria-hidden', 'true');
    rail.appendChild(document.createElement('i'));
    outer.insertBefore(rail, outer.firstChild);
    if (!pre) outer.appendChild(w);                  // 상자가 마지막 자식 — `:has(> .nxe-tw--more)` 가 산다(미리 세운 껍데기도 상자 하나만 담는다)
    if (slide) {
      rail.addEventListener('click', () => w.scrollBy({ left: w.clientWidth, behavior: 'smooth' }));
    }
    mark(w);
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => mark(w)).observe(w);
    w.addEventListener('scroll', () => {
      w.classList.toggle('nxe-tw--more', w.classList.contains('nxe-tw--scroll') && moreRight(w));
    }, { passive: true });
    // 그림이 늦게 들어와 폭이 바뀌는 슬라이드 — 그림이 올 때마다 다시 잰다
    w.addEventListener('load', () => mark(w), true);
  });
  return n;
}

let watching = false;

/** 처음 한 번 + 문서가 바뀔 때마다(한 프레임에 한 번). */
export function initHScroll(): void {
  enableHScroll(document);
  if (watching || typeof MutationObserver !== 'function') return;
  watching = true;
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; enableHScroll(document); });
  }).observe(document.body, { childList: true, subtree: true });
}
